import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Reveal } from './animations';
import AppNav from './AppNav';
import ForumAttachment from './ForumAttachment';
import { supabase } from './supabaseClient';
import './ForumPage.css';
import './Card.css';
import './ForumThreadPage.css';

const BRANCH_LOGOS = {
  'U.S. Army':         '/500px-Mark_of_the_United_States_Army.svg.png',
  'U.S. Marine Corps': '/500px-Emblem_of_the_United_States_Marine_Corps.svg.png',
  'U.S. Navy':         '/500px-Emblem_of_the_United_States_Navy.svg.png',
  'U.S. Air Force':    '/500px-U.S._Air_Force_service_mark.svg.png',
  'U.S. Space Force':  '/500px-Seal_of_the_United_States_Space_Force.svg.png',
  'U.S. Coast Guard':  '/500px-Seal_of_the_United_States_Coast_Guard.svg.png',
};

function ReplyIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" {...props}>
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function shortBranch(fullBranch) {
  return (fullBranch || '').replace('U.S. ', '');
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ForumThreadPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [userId,   setUserId]   = useState(null);
  const [post,     setPost]     = useState(null);
  const [replies,  setReplies]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending,  setSending]  = useState(false);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [isModerator, setIsModerator] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);

    const { data: postData } = await supabase
      .from('forum_posts')
      .select('id, author_id, branch, topic, occupation_code, occupation_title, title, body, created_at, attachment_url, attachment_name, attachment_type, forum_post_votes(count)')
      .eq('id', postId)
      .single();

    if (!postData) { setPost(null); setLoading(false); return; }

    const { data: repliesData } = await supabase
      .from('forum_replies')
      .select('id, author_id, body, created_at, forum_reply_votes(count)')
      .eq('post_id', postId)
      .order('created_at', { ascending: true });

    const authorIds = [...new Set([postData.author_id, ...(repliesData || []).map(r => r.author_id)])];
    const authorMap = {};
    if (authorIds.length > 0) {
      const { data: authors } = await supabase
        .from('profiles_public')
        .select('id, first_name, last_name, avatar_url, branch')
        .in('id', authorIds);
      (authors || []).forEach(a => { authorMap[a.id] = a; });
    }

    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id;
    let myPostVote = false;
    let myReplyVoteIds = new Set();
    if (uid) {
      const [{ data: pv }, { data: rv }] = await Promise.all([
        supabase.from('forum_post_votes').select('post_id').eq('user_id', uid).eq('post_id', postId).maybeSingle(),
        (repliesData || []).length > 0
          ? supabase.from('forum_reply_votes').select('reply_id').eq('user_id', uid).in('reply_id', repliesData.map(r => r.id))
          : Promise.resolve({ data: [] }),
      ]);
      myPostVote = !!pv;
      myReplyVoteIds = new Set((rv || []).map(v => v.reply_id));
    }

    setPost({
      ...postData,
      author: authorMap[postData.author_id] || null,
      voteCount: postData.forum_post_votes?.[0]?.count || 0,
      hasVoted: myPostVote,
    });
    setReplies((repliesData || []).map(r => ({
      ...r,
      author: authorMap[r.author_id] || null,
      voteCount: r.forum_reply_votes?.[0]?.count || 0,
      hasVoted: myReplyVoteIds.has(r.id),
    })));
    setLoading(false);
  }, [postId]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setUserId(session.user.id);
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  // Branch moderators can remove other members' posts/replies in their
  // branch, not just their own -- topic posts (branch is null) have no
  // moderator coverage, matching the "each branch forum" scope this was
  // built for.
  useEffect(() => {
    if (!userId || !post?.branch) { setIsModerator(false); return; }
    let cancelled = false;
    supabase
      .from('forum_moderators')
      .select('id')
      .eq('user_id', userId)
      .eq('branch', post.branch)
      .maybeSingle()
      .then(({ data }) => { if (!cancelled) setIsModerator(!!data); });
    return () => { cancelled = true; };
  }, [userId, post?.branch]);

  const handleReply = async () => {
    if (!userId || !replyText.trim() || sending || !post) return;
    setSending(true);

    const { error } = await supabase.from('forum_replies').insert({
      post_id: postId,
      author_id: userId,
      body: replyText.trim(),
    });

    if (!error) {
      setReplyText('');
      await load();

      if (post.author_id !== userId) {
        await supabase.from('notifications').insert({
          user_id:   post.author_id,
          actor_id:  userId,
          type:      'forum_reply',
          entity_id: postId,
        });
      }
    }
    setSending(false);
  };

  const handleTogglePostVote = async () => {
    if (!userId || !post) return;
    const wasVoted = post.hasVoted;
    setPost(prev => ({ ...prev, hasVoted: !wasVoted, voteCount: prev.voteCount + (wasVoted ? -1 : 1) }));
    if (wasVoted) {
      await supabase.from('forum_post_votes').delete().eq('post_id', post.id).eq('user_id', userId);
    } else {
      await supabase.from('forum_post_votes').insert({ post_id: post.id, user_id: userId });
    }
  };

  const handleToggleReplyVote = async (reply) => {
    if (!userId) return;
    const wasVoted = reply.hasVoted;
    setReplies(prev => prev.map(r => r.id === reply.id
      ? { ...r, hasVoted: !wasVoted, voteCount: r.voteCount + (wasVoted ? -1 : 1) }
      : r
    ));
    if (wasVoted) {
      await supabase.from('forum_reply_votes').delete().eq('reply_id', reply.id).eq('user_id', userId);
    } else {
      await supabase.from('forum_reply_votes').insert({ reply_id: reply.id, user_id: userId });
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    await supabase.from('forum_replies').delete().eq('id', replyId);
    setReplies(prev => prev.filter(r => r.id !== replyId));
  };

  const handleDeletePost = async () => {
    if (!post || !window.confirm('Delete this post and all its replies? This can\'t be undone.')) return;
    setDeletingPostId(post.id);
    const { error } = await supabase.from('forum_posts').delete().eq('id', post.id);
    if (!error) navigate('/forums');
    setDeletingPostId(null);
  };

  if (loading) {
    return (
      <div className="forum-root vq-surface">
        <AppNav />
        <div className="forum-shell"><div className="forum-loading vq-card" role="status"><div className="forum-spinner" /><p>Loading discussion…</p></div></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="forum-root vq-surface">
        <AppNav />
        <div className="forum-shell">
          <div className="forum-empty vq-card">
            <div className="forum-empty-icon"><ReplyIcon width="28" height="28" /></div>
            <h3>Post not found</h3>
            <p>It may have been removed. <Link to="/forums">Back to Forums</Link></p>
          </div>
        </div>
      </div>
    );
  }

  const postInitials = `${post.author?.first_name?.[0] || ''}${post.author?.last_name?.[0] || ''}`.toUpperCase() || '?';

  return (
    <div className="forum-root vq-surface">
      <AppNav />
      <div className="forum-shell">
        <Link to="/forums" className="thread-back-link">← Back to {post.topic || shortBranch(post.branch)} Forum</Link>

        <Reveal>
          <div className="thread-post-card vq-card vq-card--raised" data-branch={post.branch || post.author?.branch}>
            <div className="thread-post-top">
              <button
                className={`forum-vote-btn thread-vote-btn ${post.hasVoted ? 'active' : ''}`}
                aria-label={`${post.voteCount || 0} helpful${post.hasVoted ? ', marked helpful by you' : ''}`}
                onClick={handleTogglePostVote}
              >
                <span className="forum-vote-arrow">▲</span>
                <span className="forum-vote-count">{post.voteCount}</span>
              </button>
              <div className="forum-post-av thread-post-av">
                {post.author?.avatar_url
                  ? <img src={post.author.avatar_url} alt="avatar" className="forum-post-av-img" />
                  : postInitials
                }
              </div>
              <div>
                <div className="forum-post-meta">
                  <span className="forum-post-author">{post.author ? `${post.author.first_name} ${post.author.last_name}` : 'Member'}</span>
                  {post.topic && post.author?.branch && (
                    <span className="forum-post-branch-badge">
                      <img src={BRANCH_LOGOS[post.author.branch]} alt="" className="forum-post-branch-badge-logo" />
                      {shortBranch(post.author.branch)}
                    </span>
                  )}
                  {post.occupation_code && <span className="forum-post-afsc">{post.occupation_code}</span>}
                  <span className="forum-post-time">{timeAgo(post.created_at)}</span>
                </div>
              </div>
            </div>
            <h1 className="thread-post-title">{post.title}</h1>
            <p className="thread-post-body">{post.body}</p>
            {post.attachment_url && <ForumAttachment value={post.attachment_url} name={post.attachment_name} type={post.attachment_type} />}
            {(userId === post.author_id || isModerator) && (
              <button className="thread-delete-btn" onClick={handleDeletePost} disabled={deletingPostId === post.id}>
                {deletingPostId === post.id ? 'Deleting…' : userId === post.author_id ? 'Delete Post' : 'Remove Post (moderator)'}
              </button>
            )}
          </div>
        </Reveal>

        <div className="thread-replies-label">{replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}</div>

        {replies.length === 0 && <p className="thread-empty-replies">Replies will appear here. Use the box below to add your perspective.</p>}
        <div className="thread-reply-list">
          {replies.map(r => {
            const initials = `${r.author?.first_name?.[0] || ''}${r.author?.last_name?.[0] || ''}`.toUpperCase() || '?';
            return (
              <div key={r.id} className="thread-reply-card vq-card">
                <button
                  className={`forum-vote-btn thread-vote-btn-sm ${r.hasVoted ? 'active' : ''}`}
                  aria-label={`${r.voteCount || 0} helpful${r.hasVoted ? ', marked helpful by you' : ''}`}
                  onClick={() => handleToggleReplyVote(r)}
                >
                  <span className="forum-vote-arrow">▲</span>
                  <span className="forum-vote-count">{r.voteCount}</span>
                </button>
                <div className="forum-post-av thread-reply-av">
                  {r.author?.avatar_url
                    ? <img src={r.author.avatar_url} alt="avatar" className="forum-post-av-img" />
                    : initials
                  }
                </div>
                <div className="thread-reply-body">
                  <div className="forum-post-meta">
                    <span className="forum-post-author">{r.author ? `${r.author.first_name} ${r.author.last_name}` : 'Member'}</span>
                    <span className="forum-post-time">{timeAgo(r.created_at)}</span>
                  </div>
                  <p>{r.body}</p>
                  {(userId === r.author_id || isModerator) && (
                    <button className="thread-delete-reply-btn" onClick={() => handleDeleteReply(r.id)}>
                      {userId === r.author_id ? 'Delete' : 'Remove (moderator)'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="thread-reply-composer vq-card">
          <h2 className="thread-composer-title">Add to the conversation</h2>
          <p className="thread-composer-help">Share practical experience or ask a thoughtful follow-up.</p>
          <textarea
            aria-label="Your reply"
            className="forum-compose-textarea"
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Write a reply…"
            rows={3}
          />
          <button
            className="forum-new-post-btn"
            onClick={handleReply}
            disabled={!replyText.trim() || sending}
          >
            {sending ? 'Posting…' : 'Reply'}
          </button>
        </div>
      </div>
    </div>
  );
}
